import { WordHelp } from "@/components/lesson/WordHelp";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { MessageCircleHeart, X, Send, Volume2, VolumeX } from "lucide-react";
import { http, streamSSE } from "@/lib/api";
import { toast } from "sonner";
import { stopSpeaking } from "@/lib/buddy";
import { rehypeWordSpans, useReadAlong, wordIndexOf } from "@/lib/readAlong";
import { useProfile } from "@/context/ProfileContext";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { VoiceInput } from "@/components/buddy/VoiceInput";

const NoBuddy = ({ close }) => (
  <div className="flex-1 grid place-items-center p-8 text-center" data-testid="tutor-no-buddy">
    <div>
      <MessageCircleHeart className="w-10 h-10 mx-auto text-[#C87D53]" />
      <p className="font-display text-xl text-[#2C4A3E] mt-4">You don't have a learning buddy yet</p>
      <p className="text-sm text-stone-500 mt-2">Create a companion who helps you sort your thoughts and assignments.</p>
      <Link to="/buddy" onClick={close} data-testid="tutor-create-buddy-link" className="inline-block mt-5 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-white text-sm">Create my buddy</Link>
    </div>
  </div>
);

export const TutorWidget = () => {
  const { profile, buddy, tutorOpen, setTutorOpen, tutorContext, setTutorContext } = useProfile();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("cl_buddy_voice") === "1");
  const endRef = useRef(null);
  const bubbles = useRef({});
  const msgsRef = useRef(null);
  const ra = useReadAlong();
  const readMsg = (i, text, from = 0) => ra.play(text, buddy?.voice, bubbles.current[i], `msg-${i}-${text.length}`, from).catch(() => toast.error("Voice unavailable right now"));

  useEffect(() => { http.get("/tutor/history").then((r) => setMsgs(r.data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, tutorOpen]);
  const toggleVoice = () => { const v = !voiceOn; setVoiceOn(v); localStorage.setItem("cl_buddy_voice", v ? "1" : "0"); if (!v) { stopSpeaking(); ra.stop(); } };

  const send = async (raw, spoken) => {
    const text = (raw || "").trim();
    if (!text || busy) return;
    setInput(""); setBusy(true);
    let reply = "";
    const idx = msgs.length + 1;
    ra.stop();
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    const append = (d) => { reply += d; setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; }); };
    try {
      await streamSSE("/tutor/stream", { message: text, ...tutorContext }, append);
      if ((voiceOn || spoken) && reply) setTimeout(() => readMsg(idx, reply), 50);
    } catch { append("I'm having trouble connecting. Let's take a breath and try again."); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!tutorOpen || !tutorContext.prefill || busy) return;
    const { prefill, ...rest } = tutorContext;
    setTutorContext(rest);
    send(prefill);
  }, [tutorOpen, tutorContext.prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tutorOpen) return <BuddyFab buddy={buddy} onOpen={() => setTutorOpen(true)} />;

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[580px] max-h-[80vh] rounded-3xl bg-[#FDFBF7]/95 backdrop-blur-xl border border-[#E6DEC3] shadow-2xl flex flex-col" data-testid="tutor-panel">
      <div className="flex items-center gap-3 p-4 border-b border-[#E6DEC3]">
        <BuddyAvatar buddy={buddy} className="w-11 h-11" testId="tutor-header-avatar" />
        <div className="flex-1"><b className="text-[#2C4A3E]" data-testid="tutor-buddy-name">{buddy?.name || "Learning buddy"}</b><p className="text-xs text-stone-500">Your learning buddy</p></div>
        {buddy && <button onClick={toggleVoice} data-testid="tutor-voice-toggle" data-state={voiceOn ? "on" : "off"} aria-label="Toggle spoken replies" className="text-stone-500 hover:text-[#2C4A3E]">
          {voiceOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}</button>}
        <button onClick={() => setTutorOpen(false)} data-testid="tutor-close-btn" className="text-stone-500 hover:text-stone-800"><X className="w-5 h-5" /></button>
      </div>
      {!buddy ? <NoBuddy close={() => setTutorOpen(false)} /> : (
        <>
          <WordHelp containerRef={msgsRef} onOpen={ra.pause} />
          <div ref={msgsRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="tutor-messages" data-translate>
            {!msgs.length && <p className="text-sm text-stone-500 bg-[#F7F3EB] rounded-2xl p-4">Hey {profile.name}! It's {buddy.name}. What's on your mind today - a subject, an assignment, or just a jumble of thoughts?</p>}
            {msgs.map((m, i) => (
              <div key={i} ref={(el) => { bubbles.current[i] = el; }} data-keep-active={voiceOn ? "1" : undefined} onClick={m.role === "assistant" && voiceOn && m.content && !busy ? (e) => { const w = wordIndexOf(e); if (w != null) readMsg(i, m.content, w); } : undefined}
                className={`chat-md text-sm rounded-2xl px-4 py-2.5 max-w-[85%] ${m.role === "user" ? "ml-auto bg-[#2C4A3E] text-white" : "bg-[#F7F3EB] text-stone-700"} ${voiceOn && m.role === "assistant" ? "rw-active" : ""}`} data-testid={`tutor-msg-${m.role}`}>
                {m.content ? <ReactMarkdown rehypePlugins={m.role === "assistant" && !(busy && i === msgs.length - 1) ? [rehypeWordSpans] : []}>{m.content}</ReactMarkdown> : <span className="animate-pulse">…</span>}
                {m.role === "assistant" && m.content && voiceOn && ra.owner !== `msg-${i}-${m.content.length}` && !busy && (
                  <button onClick={(e) => { e.stopPropagation(); readMsg(i, m.content); }} data-testid={`tutor-read-msg-${i}`} className="block mt-1 text-[11px] text-[#6B9080] underline">Read aloud</button>)}
                {ra.owner === `msg-${i}-${m.content.length}` && ra.state !== "idle" && (
                  <button onClick={(e) => { e.stopPropagation(); ra.toggle(); }} data-testid={`tutor-msg-toggle-${i}`} className="block mt-1 text-[11px] text-[#6B9080] underline">{ra.state === "loading" ? "Preparing voice…" : ra.state === "playing" ? "Pause" : "Resume"}</button>)}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="p-3 border-t border-[#E6DEC3] flex gap-2">
            <VoiceInput onText={(t) => send(t, true)} />
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={`Talk to ${buddy.name}…`} data-testid="tutor-input"
              className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm" />
            <button type="submit" disabled={busy} data-testid="tutor-send-btn" className="w-10 h-10 shrink-0 rounded-full bg-[#C87D53] text-white grid place-items-center disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </form>
        </>
      )}
    </div>
  );
};

const FAB_KEY = "cl_buddy_fab";
const clampPos = (p, size) => ({ x: Math.min(Math.max(8, p.x), window.innerWidth - size - 8), y: Math.min(Math.max(72, p.y), window.innerHeight - size - 8) });

const BuddyFab = ({ buddy, onOpen }) => {
  const size = window.innerWidth < 640 ? 48 : 56;
  const [pos, setPos] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(FAB_KEY) || "null");
    return clampPos(saved || { x: window.innerWidth - size - (size === 48 ? 12 : 24), y: window.innerHeight - size - (size === 48 ? 96 : 24) }, size);
  });
  const drag = useRef(null);
  useEffect(() => { const r = () => setPos((p) => clampPos(p, size)); window.addEventListener("resize", r); return () => window.removeEventListener("resize", r); }, [size]);
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false }; };
  const move = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    d.moved = true; setPos(clampPos({ x: d.ox + dx, y: d.oy + dy }, size));
  };
  const up = () => {
    const d = drag.current; drag.current = null;
    if (!d) return;
    if (d.moved) { setPos((p) => { localStorage.setItem(FAB_KEY, JSON.stringify(p)); return p; }); } else onOpen();
  };
  return (
    <button onPointerDown={down} onDragStart={(e) => e.preventDefault()} onClick={(e) => e.detail === 0 && onOpen()} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { drag.current = null; }} data-testid="tutor-fab" aria-label="Talk to your buddy (drag to move)" title="Tap to talk · drag to move"
      style={{ left: pos.x, top: pos.y, width: size, height: size }}
      className="fixed z-50 rounded-full bg-[#2C4A3E] text-white shadow-xl grid place-items-center overflow-hidden ring-2 ring-white touch-none select-none [-webkit-user-drag:none] [&_img]:pointer-events-none cursor-grab active:cursor-grabbing transition-transform hover:scale-105">
      {buddy ? <BuddyAvatar buddy={buddy} className="w-full h-full" testId="tutor-fab-avatar" /> : <MessageCircleHeart className="w-6 h-6" />}
    </button>
  );
};
